import React, { useEffect, useMemo, useState } from 'react';
import { buildItemActivity, formatActivityTime } from '../utils/itemActivity';

// ============================================================================
// ItemActivityPanel — the recorded history of one library item
// ============================================================================
// Every save that touched the item, its versions, their classification tags or
// their rates, newest first, with who made it. Read from boq.audit_log through
// useBoqLibrary.fetchItemActivity, and followed across renumbers, so an item's
// history from before its code changed is still here.
//
// Keyed on the item code by its parent, so a renumber remounts it and re-reads.
// ============================================================================

// A long spec change: the whole old text and the whole new text, one under the
// other. Words removed are struck through in red on the Was line; words added
// are highlighted green on the Now line. Unchanged words read normally on both.
const REMOVED = { background: 'rgba(169,68,66,0.12)', color: '#a94442', textDecoration: 'line-through' };
const ADDED = { background: 'rgba(46,125,50,0.14)', color: '#2e7d32' };

function SpecChange({ label, diff }) {
  const side = (mark, style) => {
    const parts = diff.filter((d) => d.type === 'same' || d.type === mark);
    if (!parts.some((d) => d.text.trim())) {
      return <span style={{ color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>empty</span>;
    }
    return parts.map((d, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <span key={i} style={d.type === mark ? { ...style, borderRadius: 2 } : undefined}>{d.text}</span>
    ));
  };
  const row = (tag, content) => (
    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.15rem' }}>
      <span style={{ flex: '0 0 2.4rem', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--brio-text-muted)', paddingTop: '0.1rem' }}>{tag}</span>
      <span style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{content}</span>
    </div>
  );
  return (
    <div style={{ padding: '0.1rem 0 0.3rem' }}>
      <div>{label}</div>
      {row('Was', side('del', REMOVED))}
      {row('Now', side('add', ADDED))}
    </div>
  );
}

function ItemActivityPanel({ itemCode, fetchItemActivity }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchItemActivity(itemCode);
      if (cancelled) return;
      if (res.success) setRows(res.rows);
      else setError(res.error);
    })();
    return () => { cancelled = true; };
  }, [itemCode, fetchItemActivity]);

  const events = useMemo(() => (rows ? buildItemActivity(rows) : []), [rows]);

  if (error) {
    return <div style={{ padding: '0.6rem 0.9rem', background: '#fdecec', color: '#a94442', borderRadius: 6 }}>{error}</div>;
  }
  if (!rows) {
    return <div style={{ fontSize: '0.82rem', color: 'var(--brio-text-muted)', padding: '0.5rem 0' }}>Loading history…</div>;
  }
  if (!events.length) {
    return <div style={{ fontSize: '0.82rem', color: 'var(--brio-text-muted)', padding: '0.5rem 0' }}>No recorded changes for this item.</div>;
  }

  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '55vh', overflowY: 'auto' }}>
      {events.map((ev) => (
        <li key={ev.key} style={{ display: 'flex', gap: '0.75rem', padding: '0.55rem 0', borderBottom: '1px solid var(--brio-border-subtle, #eee)' }}>
          <div style={{ flex: '0 0 9.5rem', fontSize: '0.74rem', color: 'var(--brio-text-muted)' }}>
            <div>{formatActivityTime(ev.changedAt)}</div>
            <div style={{ fontWeight: 600, color: 'var(--brio-text, inherit)' }}>{ev.actorName || 'System / import'}</div>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, flex: 1, minWidth: 0, fontSize: '0.8rem' }}>
            {ev.lines.map((l, i) => (
              <li
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                style={{
                  padding: '0.05rem 0',
                  overflowWrap: 'anywhere',
                  fontWeight: l.kind === 'code' ? 600 : 400,
                  color: l.kind === 'code' ? 'var(--brio-gold, #9F902A)' : undefined,
                }}
              >
                {l.kind === 'spec' ? <SpecChange label={l.text} diff={l.diff} /> : l.text}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
}

export default ItemActivityPanel;
